/**
 * Departure-readiness backend error codes.
 *
 * Must match `ErrorCodes` in be/utils/errors.js (Departure Readiness group).
 *
 * Codes are the stable API contract; the backend's `error` string is Arabic and exists for legacy
 * clients and logs. Translation lives in the locale files (`departureReadiness.*` keys) — these are
 * just the identifiers.
 *
 * Client rule: if a response carries `code`, translate it; otherwise fall back to displaying the
 * server's message. Never infer meaning from message text.
 *
 * Twin of web/src/utils/arrivalErrorCodes.js — a separate group on purpose, so a checkout row can
 * never show check-in wording.
 */
export const DEPARTURE_ERROR_CODES = {
  BOOKING_NOT_FOUND: 'DEPARTURE_001',
  // The booking's reminder IS automated (it has an OTA thread), so a manual-send assertion is
  // refused — it would hide a real automation failure that cannot be recovered (20-minute window).
  // Seeing this means the row offering the tick was stale.
  MANUAL_SEND_NOT_APPLICABLE: 'DEPARTURE_002',
  // Checkout RECORD (tasks/manual-guest-checkout-event.md) — a different assertion from the two
  // above: those are about a MESSAGE, these about whether the guest actually left.
  // The booking is cancelled — nobody stayed, so nobody left. Note a 'completed' booking is NOT
  // refused: the completion job flips today's departures to completed at the policy hour, before
  // ops finish confirming them.
  CHECKOUT_NOT_APPLICABLE: 'DEPARTURE_003',
  // The stated departure time is missing, unparseable, in the future, or before the guest arrived.
  // Rejected, never clamped — a silently-corrected time is a fact nobody entered.
  CHECKOUT_TIME_INVALID: 'DEPARTURE_004'
};

/**
 * Reminder-column status markers (be/services/checkoutMessage/readiness.js).
 * Not error codes, but the same contract rule: the card branches on THESE, never on the Arabic copy
 * beside them.
 */
export const DEPARTURE_REMINDER_CODES = {
  // Manual (Gathern / direct) rows track the same clock the automation does, so the card never
  // invites ops to send a departure reminder five hours early — nor to compose a late one:
  //   PENDING  → due later today. No buttons.
  //   REQUIRED → due NOW. Tick + copy-the-message.
  //   MISSED   → the moment passed. Tick ONLY (ops may have sent it and not logged it); the copy
  //              button is deliberately withheld, because sending now is worse than not sending.
  MANUAL_SEND_PENDING: 'manual_send_pending',
  MANUAL_SEND_REQUIRED: 'manual_send_required',
  MANUAL_SEND_MISSED: 'manual_send_missed',
  MANUAL_SEND_DONE: 'manual_send_done',
  // An AUTOMATED send whose window closed with nothing delivered. The only record that a guest got
  // no reminder at all — rendered blunt, and never as a "retry" affordance.
  MISSED: 'missed_window'
};

/**
 * Checkout-record column markers (be/services/checkoutMessage/readiness.js).
 *
 * TWO states, not the arrival card's three — and that is not an inconsistency. Three states exist
 * because a MACHINE source can honestly fail to look; a button has only "recorded" / "not
 * recorded". A device source, when it ships, uses the three-state model in this same column.
 *
 * ⚠️ NOT_RECORDED renders as PENDING (grey), never as an action (red). This column records a fact;
 * it does not grade ops for not having captured it yet, and nothing consumes it.
 */
export const DEPARTURE_CHECKOUT_CODES = {
  RECORDED: 'checkout_recorded',
  NOT_RECORDED: 'checkout_not_recorded'
};

export default DEPARTURE_ERROR_CODES;
